import {
    Autocomplete,
    Avatar,
    Checkbox,
    CircularProgress,
    Dialog,
    IconButton,
    Paper,
    Switch,
    TextField,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Check, UserRound, UsersRound } from "lucide-react";
import * as yup from "yup";
import {
    createUserAction,
    editUserAction,
} from "../../Actions/UserActions";
import { MEDIA_URL, REACT_APP_PROFILE_FOLDER_NAME } from "../../config";
import ProfilePictureUpload from "../../components/Common/ProfilePictureUpload";
import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import { containsBadWords, containsBadWordsInEmail } from "../../utils/badWord";
import "../../components/Common/FormModal.css";
import "./AddEditUser.css";

const { VALIDATION } = FRONTEND_MESSAGES;
const SELECT_ALL_TENANTS_ID = "__select_all_tenants__";
const SELECT_ALL_TENANTS_OPTION = {
    id: SELECT_ALL_TENANTS_ID,
    firstName: "Select",
    lastName: "All",
};

const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#22419e" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#22419e",
    },
};

const PickerPaper = (props) => (
    <Paper
        {...props}
        elevation={0}
        className={`user-picker-paper ${props.className || ""}`}
    />
);

const validationCreateSchema = yup.object({
    firstName: yup
        .string()
        .trim()
        .max(50, VALIDATION.MAX_LENGTH("First Name", 50))
        .matches(/^[a-zA-Z_\-'''`–—]+$/, VALIDATION.INVALID_FORMAT("First Name"))
        .test(
            "bad-word",
            "First Name contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        )
        .required(VALIDATION.REQUIRED_FIELD("First Name")),

    lastName: yup
        .string()
        .trim()
        .max(50, VALIDATION.MAX_LENGTH("Last Name", 50))
        .matches(/^[a-zA-Z_\-'''`–—]+$/, VALIDATION.INVALID_FORMAT("Last Name"))
        .test(
            "bad-word",
            "Last Name contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        )
        .required(VALIDATION.REQUIRED_FIELD("Last Name")),

    email: yup
        .string()
        .email(VALIDATION.INVALID_EMAIL)
        .max(70, VALIDATION.MAX_LENGTH("Email", 70))
        .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            VALIDATION.INVALID_EMAIL
        )
        .test(
            "bad-email",
            "Email contains inappropriate language.",
            (value) => !containsBadWordsInEmail(value || "")
        )
        .required(VALIDATION.REQUIRED_FIELD("Email")),
    profilePic: yup
        .mixed()
        .nullable()
        .test(
            "fileType",
            VALIDATION.FILE_TYPE_INVALID(["jpeg", "png", "jpg"]),
            (value) => {
                if (!value) return true;
                const isFile = value instanceof File;
                if (!isFile) return true;
                if (value) {
                    const supportedFormats = ["jpeg", "png", "jpg"];
                    return supportedFormats.includes(
                        value.name.split(".").pop()
                    );
                }
                return true;
            }
        )
        .test(
            "fileSize",
            VALIDATION.FILE_SIZE_MAX("2 MB", "Profile picture"),
            (value) => {
                const maxFileSize = 2 * 1024 * 1024;
                if (!value) return true;
                const isFile = value instanceof File;
                if (!isFile) return true;
                return value.size <= maxFileSize;
            }
        ),
    roleId: yup
        .number()
        .required(VALIDATION.SELECT_OPTION("Role"))
        .positive(VALIDATION.INVALID_FORMAT("Role"))
        .integer(VALIDATION.INVALID_FORMAT("Role")),
    assignUserIds: yup
        .string()
        .required(VALIDATION.SELECT_OPTION("Tenant User"))
        .test("not-empty", VALIDATION.SELECT_OPTION("Tenant User"), (value) => {
            return value && value.trim().length > 0;
        }),
});

const validationUpdateSchema = yup.object({
    firstName: yup
        .string()
        .trim()
        .max(50, VALIDATION.MAX_LENGTH("First Name", 50))
        .matches(/^[a-zA-Z_\-'''`–—]+$/, VALIDATION.INVALID_FORMAT("First Name"))
        .test(
            "bad-word",
            "First Name contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        )
        .required(VALIDATION.REQUIRED_FIELD("First Name")),

    lastName: yup
        .string()
        .trim()
        .max(50, VALIDATION.MAX_LENGTH("Last Name", 50))
        .matches(/^[a-zA-Z_\-'''`–—]+$/, VALIDATION.INVALID_FORMAT("Last Name"))
        .test(
            "bad-word",
            "Last Name contains inappropriate language.",
            (value) => !containsBadWords(value || "")
        )
        .required(VALIDATION.REQUIRED_FIELD("Last Name")),

    email: yup
        .string()
        .email(VALIDATION.INVALID_EMAIL)
        .max(70, VALIDATION.MAX_LENGTH("Email", 70))
        .matches(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            VALIDATION.INVALID_EMAIL
        )
        .test(
            "bad-email",
            "Email contains inappropriate language.",
            (value) => !containsBadWordsInEmail(value || "")
        )
        .required(VALIDATION.REQUIRED_FIELD("Email")),
    profilePic: yup
        .mixed()
        .nullable()
        .test(
            "fileType",
            VALIDATION.FILE_TYPE_INVALID(["jpeg", "png", "jpg"]),
            (value) => {
                if (!value) return true;
                const isFile = value instanceof File;
                if (!isFile) return true;
                if (value) {
                    const supportedFormats = ["jpeg", "png", "jpg"];
                    return supportedFormats.includes(
                        value.name.split(".").pop()
                    );
                }
                return true;
            }
        )
        .test(
            "fileSize",
            VALIDATION.FILE_SIZE_MAX("2 MB", "Profile picture"),
            (value) => {
                const maxFileSize = 2 * 1024 * 1024;
                if (!value) return true;
                const isFile = value instanceof File;
                if (!isFile) return true;
                return value.size <= maxFileSize;
            }
        ),
    roleId: yup
        .number()
        .required(VALIDATION.SELECT_OPTION("Role"))
        .positive(VALIDATION.INVALID_FORMAT("Role"))
        .integer(VALIDATION.INVALID_FORMAT("Role")),
    assignUserIds: yup
        .string()
        .required(VALIDATION.SELECT_OPTION("Tenant User"))
        .test("not-empty", VALIDATION.SELECT_OPTION("Tenant User"), (value) => {
            return value && value.trim().length > 0;
        }),
});

const AddEditMasterUserModal = ({
    open,
    handleClose,
    masterUserDataById,
    setSnackbar,
    roles,
    merchantUser,
    setShouldFetchData,
}) => {
    const [isImageEdited, setIsImageEdited] = useState(false);
    const [buttonDisable, setButtonDisable] = useState(false);
    const [imageRemoved, setImageRemoved] = useState(false);
    const [loading, setLoading] = useState(false);
    const profileFolderName = REACT_APP_PROFILE_FOLDER_NAME || "profilePics";
    const isEdit = Boolean(masterUserDataById);

    const formik = useFormik({
        initialValues: {
            firstName: masterUserDataById?.firstName
                ? masterUserDataById.firstName
                : "",
            lastName: masterUserDataById?.lastName
                ? masterUserDataById.lastName
                : "",
            email: masterUserDataById?.email ? masterUserDataById.email : "",
            profilePic: masterUserDataById?.profilePic
                ? masterUserDataById.profilePic
                : null,
            profilePicPreview: masterUserDataById?.profilePic
                ? `${MEDIA_URL}/qbotmasterapp.netcluesdemo.com/${profileFolderName}/${masterUserDataById.profilePic}`
                : "",
            isActive: masterUserDataById ? masterUserDataById.isActive : true,
            twoFactor: masterUserDataById ? masterUserDataById.twoFactor : true,
            roleId: masterUserDataById ? masterUserDataById.roleId : "",
            assignUserIds: masterUserDataById?.assignUserIds
                ? masterUserDataById.assignUserIds.toString()
                : "",
        },
        validationSchema: masterUserDataById
            ? validationUpdateSchema
            : validationCreateSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setButtonDisable(true);
            setLoading(true);

            try {
                const formData = new FormData();

                formData.append("firstName", values.firstName);
                formData.append("lastName", values.lastName);
                formData.append("email", values.email);
                formData.append("twoFactor", values.twoFactor);
                formData.append("roleId", values.roleId);
                formData.append("assignUserIds", values.assignUserIds);

                if (masterUserDataById) {
                    formData.append("isActive", values.isActive);
                    formData.append("filename", masterUserDataById.profilePic);
                    formData.append("isAdmin", masterUserDataById.isAdmin);
                }

                if (values.profilePic) {
                    formData.append("profilePic", values.profilePic);
                }
                formData.append("isImageEdited", isImageEdited);

                if (imageRemoved) {
                    formData.append("imageRemoved", imageRemoved);
                    formData.append("isImageEdited", false);
                }

                if (masterUserDataById) {
                    const res = await editUserAction(
                        formData,
                        masterUserDataById.id
                    );
                    setSnackbar({
                        open: true,
                        message: res?.data?.message,
                        severity: "success",
                    });
                    setShouldFetchData(1);
                } else {
                    const res = await createUserAction(formData);
                    setSnackbar({
                        open: true,
                        message: res?.data?.message,
                        severity: "success",
                    });
                    setShouldFetchData(1);
                }

                setIsImageEdited(false);
                setImageRemoved(false);
                formik.resetForm();
                handleClose();
            } catch (error) {
                console.error(error, "error");
                const message = error.message ?? error.error;
                setSnackbar({
                    open: true,
                    message,
                    severity: "error",
                });
            } finally {
                setLoading(false);
                setButtonDisable(false);
            }
        },
    });

    useEffect(() => {
        if (!open) {
            setIsImageEdited(false);
            setImageRemoved(false);
            formik.resetForm();
        }
    }, [open]);

    const tenantUsers = Array.isArray(merchantUser) ? merchantUser : [];
    const selectedTenantIds = formik.values.assignUserIds
        ? formik.values.assignUserIds
              .toString()
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean)
        : [];
    const selectedTenantUsers = tenantUsers.filter((option) =>
        selectedTenantIds.includes(option.id.toString())
    );
    const allTenantUsersSelected =
        tenantUsers.length > 0 &&
        selectedTenantUsers.length === tenantUsers.length;

    const handleMerchantChange = (event, newValue) => {
        const selectAllClicked = newValue.some(
            (item) => item.id === SELECT_ALL_TENANTS_ID
        );
        const finalValue = selectAllClicked
            ? allTenantUsersSelected
                ? []
                : tenantUsers
            : newValue;
        const selectedIds = finalValue.map((item) => item.id).join(",");
        formik.setFieldValue("assignUserIds", selectedIds);
    };

    const handleFileSelect = (file) => {
        setIsImageEdited(true);
        setImageRemoved(false);
        formik.setFieldValue("profilePic", file);

        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                formik.setFieldValue("profilePicPreview", e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemovePicture = () => {
        setIsImageEdited(true);
        setImageRemoved(true);
        formik.setFieldValue("profilePic", null);
        formik.setFieldValue("profilePicPreview", "");
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: "fm-dialog" }}
        >
            <div className="fm-header">
                <span className="fm-header-icon" aria-hidden>
                    <UserRound size={20} />
                </span>
                <div className="fm-header-copy">
                    <h2>{isEdit ? "Update User" : "Add User"}</h2>
                    <p>
                        {isEdit
                            ? "Update profile details, role, and tenant access for this master user."
                            : "Create a master user with a role and the tenants they can manage."}
                    </p>
                </div>
                <IconButton
                    aria-label="close"
                    className="fm-close"
                    onClick={handleClose}
                >
                    <MyCloseIcon />
                </IconButton>
            </div>

            <form onSubmit={formik.handleSubmit} className="fm-form">
                <div className="fm-body">
                    <section className="fm-section">
                        <div className="fm-section-head">
                            <h3>Profile</h3>
                            <p>Photo and personal details for this account.</p>
                        </div>
                        <div className="fm-card">
                            <div className="fm-grid">
                                <div className="fm-field is-full">
                                    <ProfilePictureUpload
                                        onFileSelect={handleFileSelect}
                                        onRemove={handleRemovePicture}
                                        accept="image/jpeg,image/png,image/jpg"
                                        maxSize={2 * 1024 * 1024}
                                        error={
                                            formik.touched.profilePic &&
                                            formik.errors.profilePic
                                                ? formik.errors.profilePic
                                                : null
                                        }
                                        preview={
                                            formik.values.profilePicPreview
                                        }
                                        firstName={
                                            formik.values.firstName || ""
                                        }
                                        lastName={formik.values.lastName || ""}
                                        size={120}
                                    />
                                </div>

                                <div className="fm-field">
                                    <label htmlFor="user-first-name">
                                        First name
                                        <span className="fm-req">*</span>
                                    </label>
                                    <TextField
                                        id="user-first-name"
                                        placeholder="Enter first name"
                                        name="firstName"
                                        size="small"
                                        value={formik.values.firstName}
                                        onChange={(e) => {
                                            formik.setFieldValue(
                                                "firstName",
                                                e.target.value.replace(
                                                    /\s/g,
                                                    ""
                                                )
                                            );
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.firstName &&
                                            Boolean(formik.errors.firstName)
                                        }
                                        helperText={
                                            formik.touched.firstName &&
                                            formik.errors.firstName
                                        }
                                        fullWidth
                                    />
                                </div>

                                <div className="fm-field">
                                    <label htmlFor="user-last-name">
                                        Last name
                                        <span className="fm-req">*</span>
                                    </label>
                                    <TextField
                                        id="user-last-name"
                                        placeholder="Enter last name"
                                        size="small"
                                        name="lastName"
                                        value={formik.values.lastName}
                                        onChange={(e) => {
                                            formik.setFieldValue(
                                                "lastName",
                                                e.target.value.replace(
                                                    /\s/g,
                                                    ""
                                                )
                                            );
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.lastName &&
                                            Boolean(formik.errors.lastName)
                                        }
                                        helperText={
                                            formik.touched.lastName &&
                                            formik.errors.lastName
                                        }
                                        fullWidth
                                    />
                                </div>

                                <div className="fm-field is-full">
                                    <label htmlFor="user-email">
                                        Email<span className="fm-req">*</span>
                                    </label>
                                    <TextField
                                        id="user-email"
                                        placeholder="Enter email"
                                        name="email"
                                        size="small"
                                        value={formik.values.email}
                                        onChange={(e) => {
                                            formik.setFieldValue(
                                                "email",
                                                e.target.value.replace(
                                                    /\s/g,
                                                    ""
                                                )
                                            );
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={
                                            formik.touched.email &&
                                            Boolean(formik.errors.email)
                                        }
                                        helperText={
                                            formik.touched.email &&
                                            formik.errors.email
                                        }
                                        fullWidth
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="fm-section">
                        <div className="fm-section-head">
                            <h3>Access</h3>
                            <p>
                                Role permissions and the tenant users this
                                account can manage.
                            </p>
                        </div>
                        <div className="fm-card">
                            <div className="fm-grid is-stack">
                                <div className="fm-field">
                                    <label htmlFor="user-role">
                                        Role<span className="fm-req">*</span>
                                    </label>
                                    <Autocomplete
                                        id="user-role"
                                        className="user-role-picker"
                                        size="small"
                                        options={
                                            Array.isArray(roles) ? roles : []
                                        }
                                        PaperComponent={PickerPaper}
                                        value={
                                            (
                                                Array.isArray(roles)
                                                    ? roles
                                                    : []
                                            ).find(
                                                (role) =>
                                                    role.id.toString() ===
                                                    formik.values.roleId?.toString()
                                            ) || null
                                        }
                                        onChange={(event, newValue) => {
                                            formik.setFieldValue(
                                                "roleId",
                                                newValue?.id || ""
                                            );
                                            formik.setFieldTouched(
                                                "roleId",
                                                true,
                                                false
                                            );
                                        }}
                                        onBlur={() =>
                                            formik.setFieldTouched(
                                                "roleId",
                                                true
                                            )
                                        }
                                        getOptionLabel={(option) =>
                                            option?.roleName || ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id.toString() ===
                                            value.id.toString()
                                        }
                                        noOptionsText="No roles found"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Search or select role"
                                                error={
                                                    formik.touched.roleId &&
                                                    Boolean(
                                                        formik.errors.roleId
                                                    )
                                                }
                                                helperText={
                                                    formik.touched.roleId &&
                                                    formik.errors.roleId
                                                }
                                            />
                                        )}
                                    />
                                </div>

                                <div className="fm-field">
                                    <label htmlFor="user-tenants">
                                        Tenant users
                                        <span className="fm-req">*</span>
                                    </label>
                                    <Autocomplete
                                        id="user-tenants"
                                        className="tenant-user-picker"
                                        multiple
                                        disableCloseOnSelect
                                        limitTags={2}
                                        getLimitTagsText={(more) =>
                                            `+${more} selected`
                                        }
                                        size="small"
                                        options={[
                                            SELECT_ALL_TENANTS_OPTION,
                                            ...tenantUsers,
                                        ]}
                                        PaperComponent={PickerPaper}
                                        getOptionLabel={(option) => {
                                            if (
                                                option.id ===
                                                SELECT_ALL_TENANTS_ID
                                            ) {
                                                return "Select All";
                                            }
                                            return `${option.firstName} ${option.lastName}`;
                                        }}
                                        value={selectedTenantUsers}
                                        onChange={handleMerchantChange}
                                        onBlur={() =>
                                            formik.setFieldTouched(
                                                "assignUserIds",
                                                true
                                            )
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option.id.toString() ===
                                            value.id.toString()
                                        }
                                        noOptionsText="No tenant users found"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder={
                                                    selectedTenantUsers.length
                                                        ? "Search tenant users"
                                                        : "Search or select tenant users"
                                                }
                                                error={
                                                    formik.touched
                                                        .assignUserIds &&
                                                    Boolean(
                                                        formik.errors
                                                            .assignUserIds
                                                    )
                                                }
                                                helperText={
                                                    formik.touched
                                                        .assignUserIds &&
                                                    formik.errors.assignUserIds
                                                }
                                            />
                                        )}
                                        renderOption={(
                                            props,
                                            option,
                                            { selected }
                                        ) => {
                                            const isSelectAll =
                                                option.id ===
                                                SELECT_ALL_TENANTS_ID;
                                            const isChecked = isSelectAll
                                                ? allTenantUsersSelected
                                                : selected;
                                            const fullName = isSelectAll
                                                ? "Select all tenant users"
                                                : `${option.firstName} ${option.lastName}`.trim();
                                            const initials = isSelectAll
                                                ? null
                                                : `${option.firstName?.[0] || ""}${
                                                      option.lastName?.[0] || ""
                                                  }`.toUpperCase();

                                            return (
                                                <li
                                                    {...props}
                                                    key={option.id}
                                                    className={`${props.className || ""} tenant-picker-option${
                                                        isSelectAll
                                                            ? " is-select-all"
                                                            : ""
                                                    }${
                                                        isChecked
                                                            ? " is-selected"
                                                            : ""
                                                    }`}
                                                >
                                                    <Checkbox
                                                        checked={isChecked}
                                                        tabIndex={-1}
                                                        disableRipple
                                                        icon={
                                                            <span className="tenant-check" />
                                                        }
                                                        checkedIcon={
                                                            <span className="tenant-check is-checked">
                                                                <Check
                                                                    size={12}
                                                                    strokeWidth={
                                                                        3
                                                                    }
                                                                />
                                                            </span>
                                                        }
                                                    />

                                                    {isSelectAll ? (
                                                        <span className="tenant-option-avatar is-all">
                                                            <UsersRound
                                                                size={17}
                                                            />
                                                        </span>
                                                    ) : (
                                                        <Avatar className="tenant-option-avatar">
                                                            {initials || "U"}
                                                        </Avatar>
                                                    )}

                                                    <span className="tenant-option-copy">
                                                        <strong>
                                                            {fullName}
                                                        </strong>
                                                        <small>
                                                            {isSelectAll
                                                                ? `${tenantUsers.length} users available`
                                                                : option.email ||
                                                                  `Tenant user · ID ${option.id}`}
                                                        </small>
                                                    </span>

                                                    {isSelectAll ? (
                                                        <span className="tenant-select-all-action">
                                                            {allTenantUsersSelected
                                                                ? "Clear all"
                                                                : "Select all"}
                                                        </span>
                                                    ) : null}
                                                </li>
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {isEdit && (
                        <section className="fm-section">
                            <div className="fm-section-head">
                                <h3>Security</h3>
                                <p>
                                    Account status and two-factor authentication.
                                </p>
                            </div>
                            <div className="fm-card">
                                <div className="fm-toggle-list">
                                    <label
                                        className={`fm-toggle${
                                            formik.values.isActive
                                                ? " is-on"
                                                : ""
                                        }`}
                                    >
                                        <span>
                                            <p className="fm-toggle-title">
                                                Active
                                            </p>
                                            <p className="fm-toggle-sub">
                                                Inactive users cannot sign in to
                                                Master Portal.
                                            </p>
                                        </span>
                                        <Switch
                                            name="isActive"
                                            checked={Boolean(
                                                formik.values.isActive
                                            )}
                                            onChange={(event) => {
                                                formik.setFieldValue(
                                                    "isActive",
                                                    event.target.checked
                                                );
                                            }}
                                            inputProps={{
                                                "aria-label": "Active",
                                            }}
                                            sx={switchSx}
                                        />
                                    </label>

                                    <label
                                        className={`fm-toggle${
                                            formik.values.twoFactor
                                                ? " is-on"
                                                : ""
                                        }`}
                                    >
                                        <span>
                                            <p className="fm-toggle-title">
                                                Two-factor authentication
                                            </p>
                                            <p className="fm-toggle-sub">
                                                Require a second step when this
                                                user signs in.
                                            </p>
                                        </span>
                                        <Switch
                                            name="twoFactor"
                                            checked={Boolean(
                                                formik.values.twoFactor
                                            )}
                                            onChange={(event) => {
                                                formik.setFieldValue(
                                                    "twoFactor",
                                                    event.target.checked
                                                );
                                            }}
                                            inputProps={{
                                                "aria-label":
                                                    "Two-factor authentication",
                                            }}
                                            sx={switchSx}
                                        />
                                    </label>
                                </div>
                            </div>
                        </section>
                    )}
                </div>

                <div className="fm-footer">
                    <button
                        type="button"
                        className="fm-btn"
                        onClick={handleClose}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="fm-btn is-primary"
                        disabled={!formik.isValid || buttonDisable || loading}
                    >
                        {isEdit ? "Update user" : "Add user"}
                        {loading && (
                            <CircularProgress size={16} color="inherit" />
                        )}
                    </button>
                </div>
            </form>
        </Dialog>
    );
};

export default AddEditMasterUserModal;
